// The notification bell's EAGER logic (the badge, its name and the unread-count poller), kept
// free of React and the DOM so node:test covers it. The panel's list helpers live in
// list-logic.js, which only the lazy panel imports, so they stay out of the main bundle
// (notes: the slot API agreed with Support, 2026-10-31; Support's server spec §4,
// docs/superpowers/specs/2026-10-31-notification-bell-design.md).
//
// Ids are bigint on the server and may arrive as strings: they are opaque here, passed
// back exactly as received and never compared or sorted on the client.

export const POLL_MS = 60_000;

/** The badge text: nothing at 0 (or before the first count), the count, capped at 99+. */
export function badgeText(unread) {
  if (!Number.isFinite(unread) || unread <= 0) return '';
  return unread > 99 ? '99+' : String(Math.floor(unread));
}

/** The bell's accessible name. The count is in the name, not in a live region. */
export function bellLabel(unread) {
  return Number.isFinite(unread) && unread > 0
    ? `Notifications, ${unread > 99 ? 'more than 99' : Math.floor(unread)} unread`
    : 'Notifications';
}

/**
 * Whether an error from unread-count means "there is no feed here": the endpoint is missing
 * (the API's own JSON 404, code NOT_FOUND; a proxy's bare 404 is INTERNAL_ERROR and is NOT
 * gone), or, before the server half exists, a non-Support user meets a product gate on the
 * unmatched path (403 PRODUCT_NOT_LICENSED). Anything else is transient.
 */
export function isGoneError(err) {
  return (err?.status === 404 && err?.code === 'NOT_FOUND')
    || (err?.status === 403 && err?.code === 'PRODUCT_NOT_LICENSED');
}

/** No input for this long and the bell stops polling, so an idle desk's session can expire. */
export const IDLE_MS = 10 * 60_000;

/**
 * The marker the poll sends so the server answers without extending the session (Shawn,
 * 2026-10-31: "The bell doesn't keep you signed in"; agreed with Support). Sent ONLY on the
 * unread-count poll: the panel's list and mark-read calls are the person's own actions.
 */
export const BACKGROUND_POLL_HEADER = 'X-Adora-Background-Poll';

/**
 * GET /api/notifications/unread-count for the poll. Its own fetch, not the shared client:
 * it carries the marker header, and a 401 here does NOT send the tab to sign-in (an expired
 * session is found by the person's next real action). A failure throws { status, code } read
 * from the API's JSON envelope (INTERNAL_ERROR when there is none), for isGoneError.
 */
export async function fetchUnreadCount(fetchImpl, signal) {
  const res = await fetchImpl('/api/notifications/unread-count', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { [BACKGROUND_POLL_HEADER]: '1' },
    signal,
  });
  const json = (res.headers.get('content-type') ?? '').includes('application/json')
    ? await res.json().catch(() => null)
    : null;
  if (!res.ok) {
    throw Object.assign(new Error(`unread-count answered ${res.status}`), {
      status: res.status,
      code: json?.error?.code ?? 'INTERNAL_ERROR',
    });
  }
  const n = Number(json?.unread);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/**
 * The unread-count poller. Injected dependencies keep it testable:
 * - fetchCount(signal): Promise<number>; rejects with the API's error (see isGoneError);
 * - isVisible(): whether the tab is visible (no polling while hidden);
 * - isActive(): whether the person used this tab recently (no polling once idle: the
 *   session cookie is rolling, and a poll would otherwise keep an unattended desk signed
 *   in for ever). Call activity() when they come back;
 * - schedule(fn, ms) / cancel(handle): the timer;
 * - onCount(n): a count arrived;
 * - onGone(): there is no feed here (isGoneError). The bell hides and polling stops for good.
 * Other errors keep the last count and try again on the next tick, quietly.
 * A refresh that lands while a request is in flight fetches again once it settles, so a
 * count asked for after a change never comes from a request that started before it.
 */
export function createUnreadPoller({
  fetchCount, isVisible, isActive = () => true, schedule, cancel, onCount, onGone, intervalMs = POLL_MS,
}) {
  let timer = null;
  let stopped = true;
  let gone = false;
  let inFlight = null;
  let again = false;
  let controller = null;

  const clear = () => {
    if (timer !== null) cancel(timer);
    timer = null;
  };
  const canPoll = () => !stopped && !gone && isVisible() && isActive();
  const next = () => {
    clear();
    if (canPoll()) timer = schedule(tick, intervalMs);
  };

  function tick() {
    timer = null;
    if (!canPoll()) return Promise.resolve(); // resumes on visibility or activity
    if (inFlight) {
      again = true;
      return inFlight;
    }
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    controller = ctrl;
    inFlight = (async () => {
      try {
        const n = await fetchCount(ctrl?.signal);
        if (!stopped && !gone) onCount(n);
      } catch (err) {
        if (!stopped && isGoneError(err)) {
          gone = true;
          clear();
          onGone();
        }
      } finally {
        inFlight = null;
        if (controller === ctrl) controller = null;
      }
      if (again) {
        again = false;
        await tick();
      } else {
        next();
      }
    })();
    return inFlight;
  }

  return {
    start() {
      if (gone || !stopped) return;
      stopped = false;
      tick();
    },
    stop() {
      stopped = true;
      again = false;
      clear();
      controller?.abort(); // an in-flight count, and its possible 401 redirect, is dropped
      controller = null;
    },
    /** Count now (focus, the panel closing, a mark-read settling, refreshNotifications()). */
    refresh() {
      clear();
      return tick();
    },
    /** Call on visibilitychange: counts at once when the tab comes back, idles while hidden. */
    visibilityChanged() {
      if (isVisible()) return this.refresh();
      clear();
      return Promise.resolve();
    },
    /** Call when the person uses the tab after being idle: counts at once and resumes. */
    activity() {
      return this.refresh();
    },
    get gone() { return gone; },
  };
}
